<template>
    <div class="content-wrapper">
        <app-page-top-section
            :title="daily_reportData.user.full_name + ' ~ ' + formatDateToLocal(daily_reportData.report_date, true)">
            <app-default-button
                :title="$t('edit')"
                v-if="$can('update_daily_report')"
                @click="isModalActive = true"
            />
        </app-page-top-section>

        <div class="card mb-3">
            <div class="card-body">
                <h6>{{ $t('work_content') }}</h6>
                <div class="content" v-html="daily_reportData.work_content" style="white-space: break-spaces;"></div>
                <hr>

                <h6>{{ $t('problems') }}</h6>
                <div class="content" v-html="daily_reportData.problems" style="white-space: break-spaces;"></div>
                <hr>

                <h6>{{ $t('plans_for_tomorrow') }}</h6>
                <div class="content" v-html="daily_reportData.plans_for_tomorrow" style="white-space: break-spaces;"></div>
                <hr>

                <div class="row">
                    <div class="col-md-4 mb-3 mb-md-0">
                        <h6>{{ $t('status') }}</h6>
                        <div v-if="daily_reportData.status === 'rejected'" class="badge badge-pill badge-secondary">
                            {{ daily_reportData.status_name }}
                        </div>
                        <div v-else-if="daily_reportData.status === 'submitted'" class="badge badge-pill badge-success">
                            {{ daily_reportData.status_name }}
                        </div>
                        <div v-else-if="daily_reportData.status === 'reviewed'"
                             class="badge badge-pill badge-warning">
                            {{ daily_reportData.status_name }}
                        </div>
                        <div v-else class="badge badge-pill badge-primary">{{ daily_reportData.status_name }}</div>
                    </div>
                </div>

                <template v-if="daily_reportData.attachments && daily_reportData.attachments.length > 0">
                    <hr>
                    <div class="form-group">
                        <label class="font-weight-bold">{{ $t('current_files') }}</label>
                        <div class="img-container">
                            <div class="img-box" v-for="(url, index) in daily_reportData.attachments" :key="'current-image-' + index">
                                <img v-if="isImage(url)"
                                    :src="url"
                                    class="img-thumbnail"
                                    :title="getFileName(url)"
                                />
                                <div v-else
                                    :href="url"
                                    target="_blank"
                                    class="d-flex align-items-center justify-content-center text-truncate img-thumbnail"
                                    :title="getFileName(url)"
                                >
                                    <i :class="`fa fa-file-${getFileExtension(url).substring(1)}`"></i>
                                </div>
                                <div class="img-action">
                                    <button type="button" class="btn btn-sm btn-primary" @click="viewFile(url)" :title="$t('view')">
                                        <i class="fas fa-eye"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <div class="card">
            <app-overlay-loader v-if="loading"/>
            <div class="card-body">
                <form ref="form" :data-url='`${TENANT_BASE_URL}app/daily-report/comment`' @submit.prevent="submitData">
                    <div class="form-group">
                        <app-input
                            :height="100"
                            type="text-editor"
                            v-model="formData.comment"
                            :placeholder="$textAreaPlaceHolder('comment')"
                        />
                    </div>
                    <app-submit-button
                        :loading="loading"
                        :label="$t('send')"
                        @click="submitData"
                    />
                </form>

                <template v-if="commentsData.length > 0">
                    <ul class="comments mb-0 mt-5">
                        <li v-for="item in commentsData" class="comment-item">
                            <div class="avatar">
                                <img class="rounded-circle" :src="profile_picture_link(item.user)"
                                     :alt="item.user.full_name">
                            </div>
                            <div class="comment-content">
                                <div class="author" style="margin-bottom: 10px;">
                                    <b class="name mr-2" v-text="item.user.full_name"></b>
                                    <time v-text="formatDateToLocal(item.created_at, true)"></time>
                                </div>
                                <div class="content" v-html="item.comment"></div>
                            </div>
                        </li>
                    </ul>
                </template>
            </div>
        </div>

        <app-daily-report-create
            v-if="isModalActive"
            v-model="isModalActive"
            @close="isModalActive = false"
            :selected-url="`${TENANT_BASE_URL}app/daily-report/list/${daily_report.id}`"
        />
    </div>
</template>

<style scoped>
h6 {
    font-weight : bold;
}

.comments {
    list-style : none;
    padding    : 0;
}

.comments li {
    padding : 10px 0;
    display : flex;
}

.comments .avatar img {
    width        : 65px;
    margin-right : 20px;
}

.comments li:not(:last-child) {
    border-bottom : 1px solid var(--default-border-color);
}

.assigned-list {
    display   : flex;
    flex-wrap : wrap;
}

.assigned-list ul {
    display    : flex;
    gap        : 15px;
    list-style : none;
    padding    : 0;
}

.assigned-list > h6 {
    padding-right : 35px;
    width         : 130px;
}
</style>

<script>
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import HelperMixin from "../../../../common/Mixin/Global/HelperMixin";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {formatDateToLocal} from "../../../../common/Helper/Support/DateTimeHelper";
import {formDataAssigner} from "../../../../common/Helper/Support/FormHelper";
import {urlGenerator} from "../../../../common/Helper/AxiosHelper";

export default {
    name  : "AppDailyReportEdit",
    mixins: [HelperMixin, FormHelperMixins],
    props : {
        daily_report: {},
        comments    : [],
    },
    data() {
        return {
            TENANT_BASE_URL,
            isModalActive   : false,
            loading         : false,
            daily_reportData: this.daily_report,
            commentsData    : this.comments,
            formData        : {},
        }
    },
    mounted() {
        this.$hub.$on('reload-data-daily-report', (data) => this.daily_reportData = data);
    },
    methods: {
        formatDateToLocal,
        submitData() {
            this.loading = true

            let url      = this.$refs.form.dataset['url'];
            let formData = formDataAssigner(new FormData, this.formData);

            formData.set('report_id', this.daily_report.id)

            this.submitFromFixin('post', url, formData);
        },
        afterSuccess({data}) {
            this.formData = {};
            this.$emit('input', false);
            this.commentsData = [data.comment].concat(this.commentsData)
        },
        afterSuccessFromGetEditData(response) {
            this.loading = false;
        },
        profile_picture_link(user) {
            return this.$optional(user, 'profile_picture', 'full_url') || urlGenerator('/images/avatar.png')
        }
    }
}
</script>